import React, { useCallback, useEffect, useRef, useState } from "react";
import { Cap, Level } from "../utils/rbacCatalog";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import UserAvatar from "../components/atoms/UserAvatar";
import { AlertMessage } from "../components/atoms/AlertMessage";
import { Spinner } from "../components/atoms/Spinner";
import { ModalShell } from "../components/atoms/ModalShell";
import { HtmlEditorRef } from "../components/atoms/HtmlEditor";
import { LinkItem } from "../components/atoms/LinkManager";
import { SideDrawer } from "../components/atoms/SideDrawer";
import SaveContactToggle from "../components/atoms/SaveContactToggle";
import BusinessCardTemplateSelector from "../components/atoms/BusinessCardTemplateSelector";
import BusinessCardEditModal from "../components/atoms/BusinessCardEditModal";
import BusinessCardShareButton from "../components/atoms/BusinessCardShareButton";
import BusinessCardSuccessModal from "../components/molecules/BusinessCardSuccessModal";
import PersonSearch, {
  FoundPerson,
  usePersonSearch,
} from "../components/molecules/PersonSearch";
import { StatusBadge } from "../components/atoms/StatusBadge";
import UserRegistrationToggle from "../components/atoms/UserRegistrationToggle";
import ConfirmModal from "../components/public/ConfirmModal";
import { CustomButton } from "../components/atoms/CustomButton";
import { EmptyState } from "../components/atoms/EmptyState";
import FeatureUpgradePrompt from "../components/atoms/FeatureUpgradePrompt";
import { PlansModal } from "../components/modals/PlansModal";
import BusinessCardStatisticsModal from "../components/modals/BusinessCardStatisticsModal";
import {
  HiInformationCircle,
  HiOutlineCog,
  HiOutlineColorSwatch,
  HiOutlineExternalLink,
  HiOutlineIdentification,
  HiOutlinePencil,
  HiOutlineTrash,
  HiUserRemove,
  HiOutlineChartBar,
} from "react-icons/hi";
import useAuth from "../hooks/useAuth";
import {
  useAssignBusinessCard,
  useGetBusinessCardProcessId,
  useGetEntityUser,
} from "../hooks/users";
import {
  useGetEntity,
  useGetEntityPreferencesByPath,
  useUpdateEntityPreferencesByPath,
} from "../hooks/entity";
import { toast } from "react-toastify";
import HttpService from "../services/http";
import { apiCache, paginationCache } from "../utils/cacheManager";
import { PlanTier, usePermissions } from "../utils/permissions";
import {
  BusinessCardBooking,
  BusinessCardTemplate,
} from "../types/BusinessCardTypes";
import { useGetAgendaConfig, useGetAgendaTypes } from "../hooks/agenda";
import { agendaRights } from "../hooks/agenda/useAgendaAccess";
import { offersMember } from "../components/pages/dashboard/agenda/agendaTypes";
import ShowFooterToggle from "../components/atoms/ShowFooterToggle";
import StatisticsToggle from "../components/atoms/StatisticsToggle";
import AddToGroupsToggle from "../components/atoms/AddToGroupsToggle";
import { richTextLength } from "../utils/richText";

interface BusinessCardData {
  jobTitle: string;
  description?: string;
  links: LinkItem[];
  uploadedFiles?: Array<{
    fileName: string;
    cdnPath: string;
    displayName: string;
  }>;
  allowVCardDownload?: boolean;
  booking?: BusinessCardBooking;
}

interface UserWithBusinessCard {
  user_identification: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  avatar: string;
  user_role: string;
  user_type: string;
  user_account_status: string;
  hasBusinessCard: boolean;
  businessCardData?: BusinessCardData;
}

const toCardUser = ({
  user_name: _userName,
  ...person
}: FoundPerson): UserWithBusinessCard => ({
  ...person,
  hasBusinessCard: false,
  businessCardData: undefined,
});

const BusinessCard = () => {
  const { t } = useTranslation();
  const { user, customer_identification } = useAuth();
  const permissions = usePermissions(user);
  const canOfferBooking = agendaRights(permissions).canView;
  const { data: agendaTypes, isSuccess: agendaTypesLoaded } = useGetAgendaTypes(
    customer_identification,
    user.entity_identification,
    { enabled: canOfferBooking }
  );
  const { data: agendaConfig } = useGetAgendaConfig(
    customer_identification,
    user.entity_identification,
    { enabled: canOfferBooking }
  );

  const { data: ownerProfile } = useGetEntityUser({
    customer_identification,
    entityIdentification: user.entity_identification,
    user_identification: customer_identification,
  });

  const hasSaveContactAccess = permissions.isAtLeastTier(PlanTier.ENTERPRISE);
  const hasUserRegistrationAccess = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const hasFooterControlAccess = permissions.isAtLeastTier(PlanTier.PREMIUM);
  const hasStatisticsAccess = permissions.isAtLeastTier(PlanTier.ENTERPRISE);
  const hasCustomLinksAccess = permissions.hasFeatureAccess("CUSTOM_LINKS");
  const [showSaveContactUpgradeModal, setShowSaveContactUpgradeModal] =
    useState(false);
  const [
    showUserRegistrationUpgradeModal,
    setShowUserRegistrationUpgradeModal,
  ] = useState(false);
  const [showFooterUpgradeModal, setShowFooterUpgradeModal] = useState(false);
  const [showStatisticsUpgradeModal, setShowStatisticsUpgradeModal] =
    useState(false);
  const [showCustomLinksUpgradeModal, setShowCustomLinksUpgradeModal] =
    useState(false);

  type OwnerTabs = "manage" | "options";
  type RegularTabs = "manage";

  type Tabs = OwnerTabs | RegularTabs;

  const [activeTab] = useState<Tabs>("manage");
  const [showPersonChooser, setShowPersonChooser] = useState(false);
  const [showOptionsDrawer, setShowOptionsDrawer] = useState(false);
  const [showThemesDrawer, setShowThemesDrawer] = useState(false);

  const personSearch = usePersonSearch(
    customer_identification,
    user.entity_identification
  );
  const [selectedUserForCard, setSelectedUserForCard] =
    useState<UserWithBusinessCard | null>(null);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successCard, setSuccessCard] = useState<{
    name: string;
    processId: string | null;
    wasUpdate: boolean;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [showRemoveConfirmModal, setShowRemoveConfirmModal] = useState(false);
  const [userToRemove, setUserToRemove] = useState<string | null>(null);
  const [showStatisticsModal, setShowStatisticsModal] = useState(false);
  const [selectedUserForStats, setSelectedUserForStats] =
    useState<UserWithBusinessCard | null>(null);

  const [globalSaveContactEnabled, setGlobalSaveContactEnabled] =
    useState(false);

  const [globalUserRegistrationEnabled, setGlobalUserRegistrationEnabled] =
    useState(false);

  const [selectedTemplate, setSelectedTemplate] =
    useState<BusinessCardTemplate>("default");

  const [showFooter, setShowFooter] = useState<boolean>(true);

  const [saveStatistics, setSaveStatistics] = useState<boolean>(false);

  const [addToCardHolderGroups, setAddToCardHolderGroups] =
    useState<boolean>(false);

  const businessTeamCacheKey = `business_team_${customer_identification}_${user.entity_identification}`;

  const descriptionEditorRef = useRef<HtmlEditorRef | null>(null);

  const [businessCardForm, setBusinessCardForm] = useState<BusinessCardData>({
    jobTitle: "",
    description: "",
    links: [],
    uploadedFiles: [],
    allowVCardDownload: false,
  });

  const [businessTeamUsers, setBusinessTeamUsers] = useState<
    UserWithBusinessCard[]
  >([]);
  const [isLoadingBusinessTeam, setIsLoadingBusinessTeam] = useState(false);
  const [businessTeamLoaded, setBusinessTeamLoaded] = useState(false);
  const [businessTeamPagination, setBusinessTeamPagination] = useState({
    page: 0,
    limit: 25,
    total: 0,
    hasMore: false,
  });

  const [processIds, setProcessIds] = useState<Record<string, string>>({});

  const { mutateAsync: assignBusinessCard } = useAssignBusinessCard();
  const { mutateAsync: updateEntityPreferences } =
    useUpdateEntityPreferencesByPath();
  const { mutateAsync: getBusinessCardProcessId } =
    useGetBusinessCardProcessId();

  const getUserDataWithBusinessCard = useCallback(
    async (userIdentification: string) => {
      try {
        const processResponse = await getBusinessCardProcessId({
          customer_identification,
          entity_identification: user.entity_identification,
          user_identification: userIdentification,
        });

        const processId = processResponse.data.element?.process_identification;
        if (!processId) {
          return {
            hasBusinessCard: false,
            businessCardData: undefined,
            userData: null,
          };
        }

        apiCache.setWithLongTTL(
          `process_id_${customer_identification}_${user.entity_identification}_${userIdentification}`,
          processId
        );

        const processDataResponse = await HttpService.get(
          `/entity/process/${processId}`
        );

        const processInfo = processDataResponse.data?.element;
        if (processInfo.process_type !== "business_card_form") {
          return {
            hasBusinessCard: false,
            businessCardData: undefined,
            userData: null,
          };
        }

        const businessCardInfo = processInfo.process_data?.businessCardData;
        const userInfo = processInfo.process_data?.user_data;
        if (!businessCardInfo || !userInfo) {
          return {
            hasBusinessCard: false,
            businessCardData: undefined,
            userData: null,
          };
        }

        const hasJobTitle =
          businessCardInfo?.jobTitle && businessCardInfo.jobTitle.trim() !== "";

        const userData = {
          user_identification: userInfo.user_identification,
          first_name: userInfo.user_first_name || "",
          last_name: userInfo.user_last_name || "",
          email: userInfo.user_email || "",
          phone: userInfo.user_phone || "",
          avatar: userInfo.user_avatar || "",
          user_role: "",
          user_type: "",
          user_account_status: "",
        };

        return {
          hasBusinessCard: hasJobTitle,
          businessCardData: hasJobTitle ? businessCardInfo : undefined,
          userData,
        };
      } catch (error: any) {
        console.warn(
          `SUM: Error fetching fresh data for user ${userIdentification}:`,
          error
        );

        return {
          hasBusinessCard: false,
          businessCardData: undefined,
          userData: null,
        };
      }
    },
    [
      customer_identification,
      user.entity_identification,
      getBusinessCardProcessId,
    ]
  );

  const getFreshBusinessCardDataFromUMD = useCallback(
    async (userIdentification: string) => {
      try {
        const userProfileResponse = await HttpService.get(
          `/${customer_identification}/${user.entity_identification}/user/${userIdentification}/profile`
        );

        const userProfileData = userProfileResponse.data?.element;
        if (!userProfileData) {
          return {
            hasBusinessCard: false,
            businessCardData: undefined,
            userData: null,
          };
        }

        let businessCardData = null;
        let hasBusinessCard = false;

        try {
          const businessCardResponse = await HttpService.get(
            `/${customer_identification}/${user.entity_identification}/user/${userIdentification}/data/business_card_data`
          );

          businessCardData =
            businessCardResponse.data?.element?.user_data.business_card_data;

          if (
            businessCardData &&
            businessCardData.jobTitle &&
            businessCardData.jobTitle.trim() !== ""
          ) {
            hasBusinessCard = true;
          }
        } catch (businessCardError: any) {
          console.warn(
            `SUM: No business card data found for user ${userIdentification}.`
          );
        }

        const userData = {
          user_identification:
            userProfileData.user_identification || userIdentification,
          first_name: userProfileData.user_first_name || "",
          last_name: userProfileData.user_last_name || "",
          email: userProfileData.user_email || "",
          phone: userProfileData.user_phone || "",
          avatar: userProfileData.user_avatar || "",
          user_role: userProfileData.user_role || "",
          user_type: userProfileData.user_type || "",
          user_account_status: userProfileData.user_account_status || "",
        };

        return {
          hasBusinessCard,
          businessCardData: hasBusinessCard ? businessCardData : undefined,
          userData,
        };
      } catch (error: any) {
        console.error(
          `SUM: Error fetching fresh UMD data for user ${userIdentification}:`,
          error
        );

        return {
          hasBusinessCard: false,
          businessCardData: undefined,
          userData: null,
        };
      }
    },
    [customer_identification, user.entity_identification]
  );

  const invalidateBusinessTeamCache = useCallback(() => {
    paginationCache.invalidatePaginatedData(businessTeamCacheKey);
    setBusinessTeamUsers([]);
    setBusinessTeamPagination({
      page: 0,
      limit: 25,
      total: 0,
      hasMore: false,
    });
  }, [businessTeamCacheKey]);

  const fetchBusinessTeamUsers = useCallback(
    async (loadMore: boolean = false) => {
      if (isLoadingBusinessTeam) return;

      setIsLoadingBusinessTeam(true);
      try {
        const groupResponse = await apiCache.getOrSetWithMediumTTL(
          `group_${customer_identification}_${user.entity_identification}_BUSINESS_CARDS`,
          () =>
            HttpService.get(
              `/${customer_identification}/${
                user.entity_identification
              }/group/name/${encodeURIComponent("BUSINESS CARDS")}`
            )
        );

        const groupIdentification =
          groupResponse.data.element.group_identification;

        const pageToFetch = loadMore ? businessTeamPagination.page + 1 : 0;

        const cachedPageData =
          paginationCache.getPaginatedData<UserWithBusinessCard>(
            businessTeamCacheKey,
            pageToFetch
          );

        if (cachedPageData && !loadMore) {
          setBusinessTeamUsers(cachedPageData.items);
          setBusinessTeamPagination({
            page: cachedPageData.pagination.page,
            limit: cachedPageData.pagination.limit,
            total: cachedPageData.pagination.total,
            hasMore: cachedPageData.pagination.hasMore,
          });
          return;
        }

        if (cachedPageData && loadMore) {
          const existingUserIds = new Set(
            businessTeamUsers.map((u) => u.user_identification)
          );
          const newUsers = cachedPageData.items.filter(
            (user) => !existingUserIds.has(user.user_identification)
          );

          setBusinessTeamUsers((prev) => [...prev, ...newUsers]);
          setBusinessTeamPagination({
            page: cachedPageData.pagination.page,
            limit: cachedPageData.pagination.limit,
            total: cachedPageData.pagination.total,
            hasMore: cachedPageData.pagination.hasMore,
          });
          return;
        }

        const offsetToFetch = pageToFetch * businessTeamPagination.limit;

        const usersResponse = await HttpService.get(
          `/${customer_identification}/${user.entity_identification}/group/${groupIdentification}/users?limit=${businessTeamPagination.limit}&offset=${offsetToFetch}`
        );

        const usersWithBusinessCardStatus = await Promise.all(
          (usersResponse.data.element?.users || []).map(
            async (userData: any) => {
              const userCacheKey = `user_business_card_${userData.user_identification}`;
              const cachedUserData = apiCache.get(userCacheKey);

              if (cachedUserData) {
                return cachedUserData;
              }

              const freshData = await getUserDataWithBusinessCard(
                userData.user_identification
              );

              const userObj =
                freshData.userData && freshData.hasBusinessCard
                  ? {
                      ...freshData.userData,
                      user_role: userData.user_role || "",
                      user_type: userData.user_type || "",
                      user_account_status: userData.user_account_status || "",
                      hasBusinessCard: freshData.hasBusinessCard,
                      businessCardData: freshData.businessCardData,
                    }
                  : {
                      user_identification: userData.user_identification,
                      first_name: userData.user_first_name || "",
                      last_name: userData.user_last_name || "",
                      email: userData.user_email || "",
                      phone: userData.user_phone || "",
                      avatar: userData.user_avatar || "",
                      user_role: userData.user_role || "",
                      user_type: userData.user_type || "",
                      user_account_status: userData.user_account_status || "",
                      hasBusinessCard: false,
                      businessCardData: undefined,
                    };

              apiCache.setWithMediumTTL(userCacheKey, userObj);

              return userObj;
            }
          )
        );

        const pagination = usersResponse.data.element?.pagination;

        paginationCache.setPaginatedData(
          businessTeamCacheKey,
          pageToFetch,
          usersWithBusinessCardStatus,
          {
            total: pagination?.total || 0,
            limit: businessTeamPagination.limit,
            hasMore: pagination
              ? (pageToFetch + 1) * businessTeamPagination.limit <
                pagination.total
              : false,
          }
        );

        setBusinessTeamPagination({
          page: pageToFetch,
          limit: businessTeamPagination.limit,
          total: pagination?.total || 0,
          hasMore: pagination
            ? (pageToFetch + 1) * businessTeamPagination.limit <
              pagination.total
            : false,
        });

        if (loadMore) {
          setBusinessTeamUsers((prev) => [
            ...prev,
            ...usersWithBusinessCardStatus,
          ]);
        } else {
          setBusinessTeamUsers(usersWithBusinessCardStatus);
        }
      } catch (error: any) {
        if (error?.response?.status === 400) {
          setBusinessTeamUsers([]);
          setBusinessTeamPagination({
            page: 0,
            limit: 25,
            total: 0,
            hasMore: false,
          });
          invalidateBusinessTeamCache();
        } else {
          toast.error(t("businessCardPage.toast.loadBusinessCardsError"));
          console.error("SUM: Error fetching business cards users:", error);
        }
      } finally {
        setIsLoadingBusinessTeam(false);
        setBusinessTeamLoaded(true);
      }
    },
    [
      customer_identification,
      user.entity_identification,
      isLoadingBusinessTeam,
      getUserDataWithBusinessCard,
      businessTeamPagination.limit,
      businessTeamPagination.page,
      businessTeamCacheKey,
      invalidateBusinessTeamCache,
    ]
  );

  const loadMoreBusinessTeamUsers = useCallback(() => {
    if (!isLoadingBusinessTeam && businessTeamPagination.hasMore) {
      fetchBusinessTeamUsers(true);
    }
  }, [
    fetchBusinessTeamUsers,
    isLoadingBusinessTeam,
    businessTeamPagination.hasMore,
  ]);

  useEffect(() => {
    if (activeTab === "manage") {
      fetchBusinessTeamUsers();
    }
  }, [activeTab]);

  const { data: entityPreferences } = useGetEntityPreferencesByPath(
    customer_identification,
    user.entity_identification,
    "business_card_form"
  );

  const { data: entityData } = useGetEntity(
    customer_identification,
    user.entity_identification,
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    if (entityPreferences?.business_card_form?.template) {
      setSelectedTemplate(entityPreferences.business_card_form.template);
    }

    if (
      entityPreferences?.business_card_form?.enableUserRegistration !==
      undefined
    ) {
      setGlobalUserRegistrationEnabled(
        entityPreferences.business_card_form.enableUserRegistration
      );
    }

    if (
      entityPreferences?.business_card_form?.enableSaveContact !== undefined
    ) {
      setGlobalSaveContactEnabled(
        entityPreferences.business_card_form.enableSaveContact
      );
    }

    setShowFooter(
      entityPreferences?.business_card_form?.showFooter != undefined
        ? entityPreferences?.business_card_form?.showFooter
        : true
    );

    if (entityPreferences?.business_card_form?.saveStatistics !== undefined) {
      setSaveStatistics(entityPreferences.business_card_form.saveStatistics);
    }

    if (
      entityPreferences?.business_card_form?.addToCardHolderGroups !== undefined
    ) {
      setAddToCardHolderGroups(
        entityPreferences.business_card_form.addToCardHolderGroups
      );
    }
  }, [entityPreferences]);

  const handleTemplateChange = async (template: BusinessCardTemplate) => {
    setSelectedTemplate(template);

    try {
      const currentBusinessCardData =
        entityPreferences?.business_card_form || {};

      await updateEntityPreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "business_card_form",
        value: {
          ...currentBusinessCardData,
          template: template,
        },
      });

      invalidateBusinessTeamCache();
      apiCache.clear();
      if (activeTab === "manage" && businessTeamUsers.length > 0) {
        fetchBusinessTeamUsers();
      }
    } catch (error) {
      console.error("SUM: Failed to save business card template:", error);
    }
  };

  const handleShowFooterChange = async (value: boolean) => {
    setShowFooter(value);

    try {
      const currentBusinessCardData =
        entityPreferences?.business_card_form || {};

      await updateEntityPreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "business_card_form",
        value: {
          ...currentBusinessCardData,
          showFooter: value,
        },
      });

      invalidateBusinessTeamCache();
      apiCache.clear();
      if (activeTab === "manage" && businessTeamUsers.length > 0) {
        fetchBusinessTeamUsers();
      }
    } catch (error) {
      console.error("SUM: Failed to save business card template:", error);
    }
  };

  const handleSaveContactToggleChange = async (enabled: boolean) => {
    setGlobalSaveContactEnabled(enabled);

    try {
      const currentBusinessCardData =
        entityPreferences?.business_card_form || {};

      await updateEntityPreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "business_card_form",
        value: {
          ...currentBusinessCardData,
          enableSaveContact: enabled,
        },
      });

      invalidateBusinessTeamCache();
      apiCache.clear();
      if (activeTab === "manage" && businessTeamUsers.length > 0) {
        fetchBusinessTeamUsers();
      }
    } catch (error) {
      console.error("SUM: Failed to save contact button setting:", error);
    }
  };

  const handleUserRegistrationToggleChange = async (enabled: boolean) => {
    setGlobalUserRegistrationEnabled(enabled);

    try {
      const currentBusinessCardData =
        entityPreferences?.business_card_form || {};

      await updateEntityPreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "business_card_form",
        value: { ...currentBusinessCardData, enableUserRegistration: enabled },
      });

      invalidateBusinessTeamCache();
      apiCache.clear();
      if (activeTab === "manage" && businessTeamUsers.length > 0) {
        fetchBusinessTeamUsers();
      }
    } catch (error) {
      console.error("SUM: Failed to save user registration setting:", error);
    }
  };

  const handleStatisticsToggleChange = async (enabled: boolean) => {
    setSaveStatistics(enabled);

    try {
      const currentBusinessCardData =
        entityPreferences?.business_card_form || {};

      await updateEntityPreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "business_card_form",
        value: {
          ...currentBusinessCardData,
          saveStatistics: enabled,
        },
      });

      invalidateBusinessTeamCache();
      apiCache.clear();
      if (activeTab === "manage" && businessTeamUsers.length > 0) {
        fetchBusinessTeamUsers();
      }
    } catch (error) {
      console.error("SUM: Failed to save statistics setting:", error);
    }
  };

  const handleAddToGroupsToggleChange = async (enabled: boolean) => {
    setAddToCardHolderGroups(enabled);

    try {
      const currentBusinessCardData =
        entityPreferences?.business_card_form || {};

      await updateEntityPreferences({
        user_identification: customer_identification,
        entity_identification: user.entity_identification,
        path: "business_card_form",
        value: {
          ...currentBusinessCardData,
          addToCardHolderGroups: enabled,
        },
      });
    } catch (error) {
      console.error("SUM: Failed to save add-to-groups setting:", error);
    }
  };

  const handleSelectUserForBusinessCard = async (
    selectedUser: UserWithBusinessCard
  ) => {
    setShowPersonChooser(false);
    setSelectedUserForCard(selectedUser);
    setShowAssignModal(true);
    setIsLoading(true);

    const checkHasBusinessCard =
      selectedUser.hasBusinessCard ||
      businessTeamUsers.find(
        (u) => u.user_identification === selectedUser.user_identification
      )?.hasBusinessCard;

    try {
      if (checkHasBusinessCard) {
        const freshData = await getFreshBusinessCardDataFromUMD(
          selectedUser.user_identification
        );

        if (checkHasBusinessCard && freshData.businessCardData) {
          setBusinessCardForm({
            jobTitle: freshData.businessCardData.jobTitle || "",
            description: freshData.businessCardData.description || "",
            links: freshData.businessCardData.links || [],
            uploadedFiles: freshData.businessCardData.uploadedFiles || [],
            allowVCardDownload:
              freshData.businessCardData.allowVCardDownload === true,
            booking: freshData.businessCardData.booking,
          });

          if (freshData.userData) {
            setSelectedUserForCard({
              ...selectedUser,
              ...freshData.userData,
              hasBusinessCard: true,
              businessCardData: freshData.businessCardData,
            });
          }
        } else {
          if (selectedUser.businessCardData) {
            setBusinessCardForm({
              jobTitle: selectedUser.businessCardData.jobTitle || "",
              description: selectedUser.businessCardData.description || "",
              links: selectedUser.businessCardData.links || [],
              uploadedFiles: selectedUser.businessCardData.uploadedFiles || [],
              allowVCardDownload:
                selectedUser.businessCardData.allowVCardDownload === true,
              booking: selectedUser.businessCardData.booking,
            });
          } else {
            resetForm();
          }
        }
      } else {
        resetForm();
      }
    } catch (error) {
      console.error(`SUM: Error fetching fresh business card data:`, error);

      if (selectedUser.businessCardData) {
        setBusinessCardForm({
          jobTitle: selectedUser.businessCardData.jobTitle || "",
          description: selectedUser.businessCardData.description || "",
          links: selectedUser.businessCardData.links || [],
          uploadedFiles: selectedUser.businessCardData.uploadedFiles || [],
          allowVCardDownload:
            selectedUser.businessCardData.allowVCardDownload === true,
          booking: selectedUser.businessCardData.booking,
        });
      } else {
        resetForm();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const ownerAsBusinessCardUser = (): UserWithBusinessCard | null =>
    ownerProfile
      ? {
          user_identification: customer_identification,
          first_name: ownerProfile.user_first_name || "",
          last_name: ownerProfile.user_last_name || "",
          email: ownerProfile.user_email || "",
          phone: ownerProfile.user_phone || "",
          avatar: ownerProfile.user_avatar || "",
          user_role: "OWNER",
          user_type: ownerProfile.user_type || "",
          user_account_status: ownerProfile.user_account_status || "OK",
          hasBusinessCard: false,
        }
      : null;

  const openOwnerCardEditor = () => {
    const owner = ownerAsBusinessCardUser();
    if (owner) handleSelectUserForBusinessCard(owner);
  };

  const selfIntroShownRef = useRef(false);
  useEffect(() => {
    if (selfIntroShownRef.current) return;
    if (activeTab !== "manage") return;
    if (!businessTeamLoaded || isLoadingBusinessTeam) return;
    if (businessTeamUsers.length !== 0) return;
    if (permissions.isRoleGoverned()) return;
    if (!ownerProfile) return;
    selfIntroShownRef.current = true;
    openOwnerCardEditor();
  }, [
    activeTab,
    businessTeamLoaded,
    isLoadingBusinessTeam,
    businessTeamUsers.length,
    ownerProfile,
  ]);

  const handleAssignBusinessCard = async () => {
    if (!selectedUserForCard || !businessCardForm.jobTitle) {
      toast.error(t("businessCardPage.toast.requiredFieldsError"));
      return;
    }

    if (businessCardForm.jobTitle.length > 65) {
      toast.error(t("businessCardPage.toast.jobTitleLengthError"));
      return;
    }

    setIsLoading(true);

    try {
      const descriptionHtml = handleGetDescriptionHtml();

      await assignBusinessCard({
        customer_identification,
        entity_identification: user.entity_identification,
        user_identification: selectedUserForCard.user_identification,
        job_title: businessCardForm.jobTitle,
        description: descriptionHtml,
        links: businessCardForm.links,
        uploaded_files: businessCardForm.uploadedFiles,
        booking: businessCardForm.booking ?? { enabled: false, type_id: null },
      }).then(async () => {
        const assignedUser = selectedUserForCard;
        const wasUpdate = Boolean(assignedUser.hasBusinessCard);
        const action = wasUpdate
          ? t("businessCardPage.toast.assignSuccessUpdated")
          : t("businessCardPage.toast.assignSuccessAssigned");
        const fullName =
          `${assignedUser.first_name} ${assignedUser.last_name}`.trim();
        const userName = fullName || assignedUser.email;

        toast.success(
          t("businessCardPage.toast.assignSuccess", { action, name: userName }),
          {
            autoClose: 8000,
          }
        );
        setShowAssignModal(false);
        personSearch.reset();
        resetForm();

        invalidateBusinessTeamCache();
        apiCache.delete(
          `user_business_card_${assignedUser.user_identification}`
        );
        if (activeTab === "manage") {
          fetchBusinessTeamUsers();
        }

        const processId = await getProcessId(assignedUser.user_identification);
        setSuccessCard({ name: userName, processId, wasUpdate });
        setSelectedUserForCard(null);
        setShowSuccessModal(true);
      });
    } catch (error) {
      toast.error(t("businessCardPage.toast.assignError"));
    } finally {
      setIsLoading(false);
    }
  };

  const getProcessId = useCallback(
    async (userIdentification: string): Promise<string | null> => {
      const processIdCacheKey = `process_id_${customer_identification}_${user.entity_identification}_${userIdentification}`;

      const cachedProcessId = apiCache.get<string>(processIdCacheKey);
      if (cachedProcessId) {
        return cachedProcessId;
      }

      try {
        const processResponse = await getBusinessCardProcessId({
          customer_identification,
          entity_identification: user.entity_identification,
          user_identification: userIdentification,
        });

        const processId = processResponse.data.element?.process_identification;

        if (processId) {
          apiCache.setWithLongTTL(processIdCacheKey, processId);
          return processId;
        }

        return null;
      } catch (error) {
        return null;
      }
    },
    [
      getBusinessCardProcessId,
      customer_identification,
      user.entity_identification,
    ]
  );

  const isHtmlEmpty = (value: string) => richTextLength(value) === 0;

  const handleGetDescriptionHtml = () => {
    if (descriptionEditorRef.current) {
      const htmlContent = descriptionEditorRef.current.getHtml();
      return htmlContent;
    }
    return businessCardForm.description || "";
  };

  const handleLinksSubmit = async (data: { links: LinkItem[] }) => {
    setBusinessCardForm({ ...businessCardForm, links: data.links });
    return Promise.resolve();
  };

  const resetForm = () => {
    setBusinessCardForm({
      jobTitle: "",
      description: "",
      links: [],
      uploadedFiles: [],
      allowVCardDownload: false,
    });
    if (descriptionEditorRef.current) {
      descriptionEditorRef.current.setHtml("");
    }
  };

  const handlePreviewBusinessCard = (processId: string) => {
    const businessCardUrl = `${process.env.REACT_APP_HOST_URL}/business-card/${processId}`;
    window.open(businessCardUrl, "_blank");
  };

  useEffect(() => {
    const fetchProcessIds = async () => {
      if (businessTeamUsers.length === 0) return;

      const newProcessIds: Record<string, string> = {};

      await Promise.all(
        businessTeamUsers
          .filter(
            (user) =>
              user.hasBusinessCard && !processIds[user.user_identification]
          )
          .map(async (user) => {
            try {
              const processId = await getProcessId(user.user_identification);
              if (processId) {
                newProcessIds[user.user_identification] = processId;
              }
            } catch (error) {
              console.error(
                `SUM: Failed to get processId for ${user.user_identification}:`,
                error
              );
            }
          })
      );

      if (Object.keys(newProcessIds).length > 0) {
        setProcessIds((prev) => ({ ...prev, ...newProcessIds }));
      }
    };

    fetchProcessIds();
  }, [businessTeamUsers, processIds, getProcessId]);

  const handleRemoveBusinessCardClick = (userIdentification: string) => {
    setUserToRemove(userIdentification);
    setShowRemoveConfirmModal(true);
  };

  const handleShowStatistics = (user: UserWithBusinessCard) => {
    setSelectedUserForStats(user);
    setShowStatisticsModal(true);
  };

  const handleRemoveBusinessCard = async () => {
    if (!userToRemove) return;
    if (!permissions.can(Cap.BUSINESS_CARDS, Level.DELETE)) return;

    try {
      const groupResponse = await HttpService.get(
        `/${customer_identification}/${
          user.entity_identification
        }/group/name/${encodeURIComponent("BUSINESS CARDS")}`
      );

      const groupIdentification =
        groupResponse.data.element.group_identification;

      await HttpService.delete(
        `/${customer_identification}/${user.entity_identification}/user/${userToRemove}/group/${groupIdentification}`
      );

      toast.success(t("businessCardPage.toast.removeSuccess"));

      invalidateBusinessTeamCache();
      apiCache.delete(`user_business_card_${userToRemove}`);
      setProcessIds((prev) => {
        const newProcessIds = { ...prev };
        delete newProcessIds[userToRemove];
        return newProcessIds;
      });
      if (activeTab === "manage") {
        fetchBusinessTeamUsers();
      }

      setShowRemoveConfirmModal(false);
      setUserToRemove(null);
    } catch (error) {
      toast.error(t("businessCardPage.toast.removeError"));
      setShowRemoveConfirmModal(false);
      setUserToRemove(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>{t("dashboard.layout.item-15")} - Super User Manager</title>
      </Helmet>

      <div className="container mx-auto py-3 sm:py-4 md:py-6 px-3 sm:px-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">
              {t("dashboard.layout.item-15")}
            </h1>
            <p className="mt-0.5 text-sm text-gray-500">
              {t("businessCardPage.header.description")}
            </p>
          </div>
        </div>

        <AlertMessage
          color="info"
          icon={HiInformationCircle}
          className="mb-4 sm:mb-6"
        >
          {t("businessCardPage.alert.description")}
          <br />
          <span className="text-sm mt-2 block">
            <strong>{t("businessCardPage.alert.note")}</strong>{" "}
            {t("businessCardPage.alert.noteDescription")}
          </span>
        </AlertMessage>

        <div className="mb-4 sm:mb-6">
          {permissions.can(Cap.BUSINESS_CARDS_ATTRIBUTION, Level.WRITE) && (
            <ModalShell
              show={showPersonChooser}
              onClose={() => setShowPersonChooser(false)}
              size="4xl"
              title={t("businessCardPage.chooser.assignTitle")}
              subtitle={t("businessCardPage.chooser.assignSubtitle")}
            >
              <PersonSearch
                state={personSearch}
                renderAction={(person) =>
                  permissions.can(Cap.BUSINESS_CARDS, Level.WRITE) ||
                  permissions.can(
                    Cap.BUSINESS_CARDS_ATTRIBUTION,
                    Level.WRITE
                  ) ? (
                    <CustomButton
                      variant="primary"
                      size="sm"
                      icon="pencilWhite"
                      onClick={() =>
                        handleSelectUserForBusinessCard(toCardUser(person))
                      }
                      title={t("businessCardPage.searchTab.manageCardButton")}
                    />
                  ) : null
                }
              />
            </ModalShell>
          )}

          {activeTab === "manage" && (
            <div className="bg-white rounded-lg border border-gray-200">
              <div className="p-3 sm:p-4 border-b">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 sm:gap-4">
                  <div>
                    <h3 className="text-base sm:text-lg font-medium">
                      {t("businessCardPage.manageTab.title")}
                    </h3>
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {permissions.can(
                      Cap.BUSINESS_CARDS_ATTRIBUTION,
                      Level.WRITE
                    ) && (
                      <CustomButton
                        variant="primary"
                        size="sm"
                        icon="userPlus"
                        onClick={() => setShowPersonChooser(true)}
                        title={t("businessCardPage.chooser.assignTitle")}
                      />
                    )}
                    {permissions.can(
                      Cap.BUSINESS_CARDS_OPTIONS,
                      Level.WRITE
                    ) && (
                      <>
                        <CustomButton
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowOptionsDrawer(true)}
                          tooltip={
                            t(
                              "businessCardPage.manageTab.optionsButton"
                            ) as string
                          }
                          comp={
                            <span className="flex items-center gap-2">
                              <HiOutlineCog size={18} />
                              <span className="hidden sm:inline">
                                {t("businessCardPage.manageTab.optionsButton")}
                              </span>
                            </span>
                          }
                        />
                        <CustomButton
                          variant="secondary"
                          size="sm"
                          onClick={() => setShowThemesDrawer(true)}
                          tooltip={
                            t(
                              "businessCardPage.manageTab.themesButton"
                            ) as string
                          }
                          comp={
                            <span className="flex items-center gap-2">
                              <HiOutlineColorSwatch size={18} />
                              <span className="hidden sm:inline">
                                {t("businessCardPage.manageTab.themesButton")}
                              </span>
                            </span>
                          }
                        />
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="p-3 sm:p-4">
                {isLoadingBusinessTeam ? (
                  <div className="text-center py-8 sm:py-12">
                    <Spinner size="lg" />
                    <p className="mt-2 text-sm sm:text-base text-gray-600">
                      {t("businessCardPage.manageTab.loading")}
                    </p>
                  </div>
                ) : businessTeamUsers.length === 0 ? (
                  !permissions.isRoleGoverned() ? (
                    <EmptyState
                      icon={<HiOutlineIdentification size={24} />}
                      title={t("businessCardPage.manageTab.selfEmptyTitle")}
                      description={t(
                        "businessCardPage.manageTab.selfEmptyDescription"
                      )}
                      action={
                        <div className="flex flex-col items-center gap-3">
                          <CustomButton
                            variant="primary"
                            icon="userPlus"
                            onClick={openOwnerCardEditor}
                            disabled={!ownerProfile}
                            title={t("businessCardPage.manageTab.createMyCard")}
                          />
                          {permissions.can(
                            Cap.BUSINESS_CARDS_ATTRIBUTION,
                            Level.WRITE
                          ) && (
                            <button
                              type="button"
                              onClick={() => setShowPersonChooser(true)}
                              className="text-sm text-gray-500 hover:text-gray-800 underline underline-offset-2 transition-colors"
                            >
                              {t("businessCardPage.manageTab.orAssignTeam")}
                            </button>
                          )}
                        </div>
                      }
                    />
                  ) : (
                    <EmptyState
                      icon={<HiOutlineIdentification size={24} />}
                      title={t("businessCardPage.manageTab.noMembersTitle")}
                      description={t(
                        "businessCardPage.manageTab.noMembersDescription"
                      )}
                      action={
                        permissions.can(
                          Cap.BUSINESS_CARDS_ATTRIBUTION,
                          Level.WRITE
                        ) ? (
                          <CustomButton
                            variant="primary"
                            icon="userPlus"
                            onClick={() => setShowPersonChooser(true)}
                            title={t("businessCardPage.manageTab.assignButton")}
                          />
                        ) : undefined
                      }
                    />
                  )
                ) : (
                  <div>
                    {businessTeamPagination.hasMore && (
                      <div className="mb-3 sm:mb-4 text-sm text-gray-600">
                        {t("businessCardPage.manageTab.showingCount", {
                          current: businessTeamUsers.length,
                          total: businessTeamPagination.total,
                        })}
                      </div>
                    )}

                    <ul className="divide-y divide-gray-100">
                      {businessTeamUsers.map(
                        (teamUser: UserWithBusinessCard) => {
                          const fullName =
                            `${teamUser.first_name} ${teamUser.last_name}`.trim();
                          const displayName = fullName || teamUser.email;
                          const secondaryLine =
                            teamUser.businessCardData?.jobTitle ||
                            (fullName ? teamUser.email : "");
                          return (
                            <li
                              key={teamUser.user_identification}
                              className="flex flex-col gap-3 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between"
                            >
                              <div className="flex min-w-0 flex-1 items-center gap-3 sm:basis-64">
                                <UserAvatar
                                  avatar={teamUser.avatar || undefined}
                                  firstName={teamUser.first_name}
                                  lastName={teamUser.last_name}
                                  email={teamUser.email}
                                  sizeClass="w-10 h-10"
                                />
                                <div className="min-w-0">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <span
                                      className="truncate text-sm font-semibold text-gray-900"
                                      title={displayName}
                                    >
                                      {displayName}
                                    </span>
                                    {!teamUser.hasBusinessCard && (
                                      <StatusBadge color="gray" dot size="xs">
                                        {t(
                                          "businessCardPage.manageTab.statusNoCard"
                                        )}
                                      </StatusBadge>
                                    )}
                                  </div>
                                  {secondaryLine && (
                                    <p
                                      className="truncate text-xs text-gray-500"
                                      title={secondaryLine}
                                    >
                                      {secondaryLine}
                                    </p>
                                  )}
                                </div>
                              </div>

                              <div className="flex flex-wrap items-center gap-1.5 sm:flex-shrink-0 sm:justify-end sm:gap-2">
                                {teamUser.hasBusinessCard &&
                                  processIds[teamUser.user_identification] && (
                                    <CustomButton
                                      variant="secondary"
                                      size="sm"
                                      tooltip={
                                        t(
                                          "businessCardPage.manageTab.previewButton"
                                        ) as string
                                      }
                                      onClick={() =>
                                        handlePreviewBusinessCard(
                                          processIds[
                                            teamUser.user_identification
                                          ]
                                        )
                                      }
                                      comp={
                                        <div className="flex items-center">
                                          <HiOutlineExternalLink
                                            className="sm:mr-2"
                                            size={14}
                                          />
                                          <span className="hidden sm:inline">
                                            {t(
                                              "businessCardPage.manageTab.previewButton"
                                            )}
                                          </span>
                                        </div>
                                      }
                                    />
                                  )}
                                {teamUser.hasBusinessCard &&
                                  processIds[teamUser.user_identification] && (
                                    <BusinessCardShareButton
                                      processId={
                                        processIds[teamUser.user_identification]
                                      }
                                      size="sm"
                                      variant="secondary"
                                      logoUrl={
                                        entityPreferences?.cdn_logo_project_path
                                      }
                                    />
                                  )}
                                {saveStatistics &&
                                  teamUser.hasBusinessCard &&
                                  permissions.can(
                                    Cap.BUSINESS_CARDS_STATS,
                                    Level.VIEW
                                  ) && (
                                    <CustomButton
                                      variant="secondary"
                                      size="sm"
                                      tooltip={
                                        t(
                                          "businessCardPage.manageTab.statisticsButton"
                                        ) as string
                                      }
                                      onClick={() =>
                                        handleShowStatistics(teamUser)
                                      }
                                      comp={
                                        <div className="flex items-center">
                                          <HiOutlineChartBar
                                            className="sm:mr-2"
                                            size={14}
                                          />
                                          <span className="hidden sm:inline">
                                            {t(
                                              "businessCardPage.manageTab.statisticsButton"
                                            )}
                                          </span>
                                        </div>
                                      }
                                    />
                                  )}
                                {(permissions.can(
                                  Cap.BUSINESS_CARDS,
                                  Level.WRITE
                                ) ||
                                  permissions.can(
                                    Cap.BUSINESS_CARDS_ATTRIBUTION,
                                    Level.WRITE
                                  )) && (
                                  <CustomButton
                                    variant="gray"
                                    size="sm"
                                    tooltip={t("common.edit") as string}
                                    onClick={() =>
                                      handleSelectUserForBusinessCard(teamUser)
                                    }
                                    disabled={!teamUser.hasBusinessCard}
                                    comp={
                                      <div className="flex items-center">
                                        <HiOutlinePencil
                                          className="sm:mr-2"
                                          size={14}
                                        />
                                        <span className="hidden sm:inline">
                                          {t("common.edit")}
                                        </span>
                                      </div>
                                    }
                                  />
                                )}
                                {permissions.can(
                                  Cap.BUSINESS_CARDS,
                                  Level.DELETE
                                ) && (
                                  <CustomButton
                                    variant="danger"
                                    size="sm"
                                    tooltip={t("common.remove") as string}
                                    onClick={() =>
                                      handleRemoveBusinessCardClick(
                                        teamUser.user_identification
                                      )
                                    }
                                    disabled={!teamUser.hasBusinessCard}
                                    comp={
                                      <div className="flex items-center">
                                        <HiOutlineTrash
                                          className="sm:mr-2"
                                          size={14}
                                        />
                                        <span className="hidden sm:inline">
                                          {t("common.remove")}
                                        </span>
                                      </div>
                                    }
                                  />
                                )}
                              </div>
                            </li>
                          );
                        }
                      )}
                    </ul>

                    {businessTeamPagination.hasMore && (
                      <div className="text-center mt-4 sm:mt-6">
                        <CustomButton
                          variant="gray"
                          className="mx-auto"
                          onClick={loadMoreBusinessTeamUsers}
                          disabled={isLoadingBusinessTeam}
                          isLoading={isLoadingBusinessTeam}
                          title={
                            isLoadingBusinessTeam
                              ? t("businessCardPage.manageTab.loadingMore")
                              : t("businessCardPage.manageTab.loadMore", {
                                  remaining:
                                    businessTeamPagination.total -
                                    businessTeamUsers.length,
                                })
                          }
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {permissions.can(Cap.BUSINESS_CARDS_OPTIONS, Level.WRITE) && (
            <>
              <SideDrawer
                open={showOptionsDrawer}
                onClose={() => setShowOptionsDrawer(false)}
                title={t("businessCardPage.optionsTab.title") as string}
                icon={HiOutlineCog}
              >
                <p className="mb-4 text-sm text-gray-500">
                  {t("businessCardPage.optionsTab.description")}
                </p>

                <div className="shrink-0 divide-y divide-gray-100 overflow-hidden rounded-lg border border-gray-200">
                  {!hasSaveContactAccess ? (
                    <div className="p-4">
                      <FeatureUpgradePrompt
                        featureName={
                          t(
                            "permissions.businessCard.saveContact.title"
                          ) as string
                        }
                        featureDescription={
                          t(
                            "permissions.businessCard.saveContact.description"
                          ) as string
                        }
                        benefits={[
                          t(
                            "permissions.businessCard.saveContact.benefit1"
                          ) as string,
                          t(
                            "permissions.businessCard.saveContact.benefit2"
                          ) as string,
                          t(
                            "permissions.businessCard.saveContact.benefit3"
                          ) as string,
                        ]}
                        onUpgrade={() => setShowSaveContactUpgradeModal(true)}
                        className="mb-0"
                      />
                    </div>
                  ) : (
                    <div className="px-4">
                      <SaveContactToggle
                        enabled={globalSaveContactEnabled}
                        onChange={handleSaveContactToggleChange}
                        description={
                          t(
                            "businessCardPage.optionsTab.saveContactHelp"
                          ) as string
                        }
                      />
                    </div>
                  )}

                  {!hasUserRegistrationAccess ? (
                    <div className="p-4">
                      <FeatureUpgradePrompt
                        featureName={
                          t(
                            "permissions.businessCard.userRegistration.title"
                          ) as string
                        }
                        featureDescription={
                          t(
                            "permissions.businessCard.userRegistration.description"
                          ) as string
                        }
                        benefits={[
                          t(
                            "permissions.businessCard.userRegistration.benefit1"
                          ) as string,
                          t(
                            "permissions.businessCard.userRegistration.benefit2"
                          ) as string,
                          t(
                            "permissions.businessCard.userRegistration.benefit3"
                          ) as string,
                          t(
                            "permissions.businessCard.userRegistration.benefit4"
                          ) as string,
                        ]}
                        onUpgrade={() =>
                          setShowUserRegistrationUpgradeModal(true)
                        }
                        className="mb-0"
                      />
                    </div>
                  ) : (
                    <div className="px-4">
                      <UserRegistrationToggle
                        enabled={globalUserRegistrationEnabled}
                        onChange={handleUserRegistrationToggleChange}
                        description={
                          t(
                            "businessCardPage.optionsTab.userRegistrationHelp"
                          ) as string
                        }
                      />
                    </div>
                  )}

                  {!hasFooterControlAccess ? (
                    <div className="p-4">
                      <FeatureUpgradePrompt
                        featureName={
                          t(
                            "businessCardPage.optionsTab.footerVisibility.title"
                          ) as string
                        }
                        featureDescription={
                          t(
                            "businessCardPage.optionsTab.footerVisibility.description"
                          ) as string
                        }
                        benefits={[
                          t(
                            "businessCardPage.optionsTab.footerVisibility.benefit1"
                          ) as string,
                        ]}
                        onUpgrade={() => setShowFooterUpgradeModal(true)}
                        className="mb-0"
                      />
                    </div>
                  ) : (
                    <div className="px-4">
                      <ShowFooterToggle
                        enabled={showFooter}
                        onChange={handleShowFooterChange}
                        description={
                          t(
                            "businessCardPage.optionsTab.footerVisibility.help"
                          ) as string
                        }
                      />
                    </div>
                  )}

                  {!hasStatisticsAccess ? (
                    <div className="p-4">
                      <FeatureUpgradePrompt
                        featureName={
                          t(
                            "businessCardPage.optionsTab.statistics.title"
                          ) as string
                        }
                        featureDescription={
                          t(
                            "businessCardPage.optionsTab.statistics.description"
                          ) as string
                        }
                        benefits={[
                          t(
                            "businessCardPage.optionsTab.statistics.benefit1"
                          ) as string,
                          t(
                            "businessCardPage.optionsTab.statistics.benefit2"
                          ) as string,
                          t(
                            "businessCardPage.optionsTab.statistics.benefit3"
                          ) as string,
                        ]}
                        onUpgrade={() => setShowStatisticsUpgradeModal(true)}
                        className="mb-0"
                      />
                    </div>
                  ) : (
                    <div className="px-4">
                      <StatisticsToggle
                        enabled={saveStatistics}
                        onChange={handleStatisticsToggleChange}
                        description={
                          t(
                            "businessCardPage.optionsTab.statistics.help"
                          ) as string
                        }
                      />
                    </div>
                  )}

                  <div className="px-4">
                    <AddToGroupsToggle
                      enabled={addToCardHolderGroups}
                      onChange={handleAddToGroupsToggleChange}
                      description={
                        t(
                          "businessCardPage.optionsTab.addToGroups.help"
                        ) as string
                      }
                    />
                  </div>
                </div>
              </SideDrawer>

              <SideDrawer
                open={showThemesDrawer}
                onClose={() => setShowThemesDrawer(false)}
                title={t("businessCardPage.optionsTab.themesTitle") as string}
                icon={HiOutlineColorSwatch}
              >
                <BusinessCardTemplateSelector
                  selectedTemplate={selectedTemplate}
                  onTemplateChange={handleTemplateChange}
                  title={
                    t("businessCardPage.optionsTab.templateTitle") as string
                  }
                  description={
                    t(
                      "businessCardPage.optionsTab.templateDescription"
                    ) as string
                  }
                  showAsCard={true}
                />
              </SideDrawer>
            </>
          )}
        </div>

        <BusinessCardEditModal
          show={showAssignModal}
          onClose={() => setShowAssignModal(false)}
          selectedUser={selectedUserForCard}
          businessCardForm={businessCardForm}
          onFormChange={setBusinessCardForm}
          onLinksSubmit={handleLinksSubmit}
          onAssignBusinessCard={handleAssignBusinessCard}
          onResetForm={resetForm}
          isLoading={isLoading}
          customerIdentification={customer_identification}
          entityIdentification={user.entity_identification}
          descriptionEditorRef={descriptionEditorRef}
          isHtmlEmpty={isHtmlEmpty}
          entityPreferences={entityPreferences}
          entityData={{
            entity_name: entityData?.entity_name,
            entity_description: entityData?.entity_description,
            entity_logo: entityPreferences?.cdn_logo_project_path,
          }}
          hasCustomLinksAccess={hasCustomLinksAccess}
          onUpgradeClick={() => setShowCustomLinksUpgradeModal(true)}
          canAssign={
            permissions.can(Cap.BUSINESS_CARDS, Level.WRITE) ||
            permissions.can(Cap.BUSINESS_CARDS_ATTRIBUTION, Level.WRITE)
          }
          canOfferBooking={canOfferBooking}
          agendaOff={agendaConfig?.enabled === false}
          bookingTypes={
            !agendaTypesLoaded || !agendaTypes
              ? undefined
              : selectedUserForCard
              ? agendaTypes.filter((type) =>
                  offersMember(type, selectedUserForCard.user_identification)
                )
              : []
          }
        />

        <BusinessCardSuccessModal
          show={showSuccessModal}
          onClose={() => {
            setShowSuccessModal(false);
            setSuccessCard(null);
          }}
          processId={successCard?.processId ?? null}
          wasUpdate={successCard?.wasUpdate}
        />

        <ConfirmModal
          show={showRemoveConfirmModal}
          onClose={() => setShowRemoveConfirmModal(false)}
          title={t("businessCardPage.removeModal.title")}
          description={t("businessCardPage.removeModal.description")}
          cancelBtnName={t("common.cancel")}
          cancelBtnFn={() => {
            setShowRemoveConfirmModal(false);
            setUserToRemove(null);
          }}
          confirmBtnName={t("common.remove")}
          confirmBtnFn={handleRemoveBusinessCard}
          ConfirmBtnColor="red"
          icon={<HiUserRemove size={24} className="text-red-500" />}
          iconBgColor="bg-red-100"
          iconColor="text-red-500"
        />

        <PlansModal
          showPlansModal={showSaveContactUpgradeModal}
          setShowPlansModal={setShowSaveContactUpgradeModal}
          targetPlan={PlanTier.ENTERPRISE}
          hideFooter={false}
        />

        <PlansModal
          showPlansModal={showUserRegistrationUpgradeModal}
          setShowPlansModal={setShowUserRegistrationUpgradeModal}
          targetPlan={PlanTier.PREMIUM}
          hideFooter={false}
        />

        <PlansModal
          showPlansModal={showFooterUpgradeModal}
          setShowPlansModal={setShowFooterUpgradeModal}
          targetPlan={PlanTier.PREMIUM}
          hideFooter={false}
        />

        <PlansModal
          showPlansModal={showStatisticsUpgradeModal}
          setShowPlansModal={setShowStatisticsUpgradeModal}
          targetPlan={PlanTier.ENTERPRISE}
          hideFooter={false}
        />

        <PlansModal
          showPlansModal={showCustomLinksUpgradeModal}
          setShowPlansModal={setShowCustomLinksUpgradeModal}
          targetPlan={PlanTier.PREMIUM}
          hideFooter={false}
        />

        {selectedUserForStats && (
          <BusinessCardStatisticsModal
            show={showStatisticsModal}
            onClose={() => {
              setShowStatisticsModal(false);
              setSelectedUserForStats(null);
            }}
            userIdentification={selectedUserForStats.user_identification}
            userName={`${selectedUserForStats.first_name} ${selectedUserForStats.last_name}`}
          />
        )}
      </div>
    </>
  );
};

export default BusinessCard;
